// OpenAI Realtime API v2 の WebSocket クライアント (spec 2026-08-29 §3.2)。
// streamingOpenAiSttClient.ts と同じ作法 — token を fetch してから subprotocol に載せ、
// input_audio_buffer.append に base64 の PCM16 を送る。違いは (a) モデルの音声と
// ツール呼び出しを受ける (b) session.update で instructions と tools を差し替える。

import { upsample16kTo24k } from "../audio/resample";
import { REALTIME_VOICE } from "../conversation/realtimeModel";

const TOKEN_ENDPOINT = "/api/realtime/token";

/**
 * `?realtime-debug=1` のときだけ WS の送受信を console に出す (既定 off、production は無音)。
 * **R2 のログには残らないものがここでしか見えない** — とくに上流が返す error は
 * events.jsonl に型として存在せず、「なぜ話し始めないのか」は到着順でしか分からない。
 * `?scribe-debug=1` / `?event-debug=1` と同じ流儀で、module-top const にしない
 * (テストが history.replaceState で差し替えられるようにするため)。
 */
function debugOn(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("realtime-debug") === "1";
}

function dlog(dir: "→" | "←", type: string, detail?: unknown): void {
  if (!debugOn()) return;
  if (detail === undefined) console.log(`[realtime] ${dir} ${type}`);
  else console.log(`[realtime] ${dir} ${type}`, detail);
}
// 24kHz 換算で 1 秒ぶんずつ送る (streamingOpenAiSttClient の APPEND_MAX_SAMPLES と同じ)
const APPEND_MAX_SAMPLES = 24000;
const ERROR_MSG_CAP = 200;

export type RealtimeToolCall = { name: string; args: string; callId: string };

export type RealtimeSessionPatch = {
  instructions: string;
  tools: unknown[];
  /** 発話終端とみなす無音の長さ。設問タイプ別 (redemptionMsForQuestionType)。 */
  silenceDurationMs: number;
  voice?: string;
};

export type RealtimeClientOpts = {
  onToolCall: (c: RealtimeToolCall) => void;
  onAudioDelta: (pcm16: Int16Array) => void;
  onTranscript: (text: string) => void;
  /**
   * **モデルが読み上げた文**。cascade は ttsPhrases.ts から復元できるが、realtime は
   * 毎回生成なので残さないと復元できない (spec D10)。入力の書き起こし
   * (onTranscript) と取り違えないこと — 取り違えると「設問文が改変されたか」を
   * 数えるつもりで回答文を数えることになる。
   */
  onNarrated: (text: string) => void;
  /** response.done の usage。実コストの一次データ (見積もりを実測に置き換える)。 */
  onUsage: (usage: Record<string, unknown>) => void;
  /**
   * **AI が話し終えた**。無音の計測を張る唯一の契機 — 発話の開始や終了で張ると、
   * 話している最中にカウントが進んで割り込む (2026-08-05 のパイロットで cascade が
   * 実測した欠陥。`SilenceTimer.reset()` のコメントに経緯がある)。
   */
  onResponseDone: () => void;
  onSpeechStarted: () => void;
  onSpeechStopped: () => void;
  onError: (e: { code: string; message: string }) => void;
};

function toBase64Pcm16(pcm: Float32Array): string {
  const i16 = new Int16Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) {
    const v = Math.max(-1, Math.min(1, pcm[i]));
    i16[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
  }
  const bytes = new Uint8Array(i16.buffer);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

function fromBase64Pcm16(b64: string): Int16Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Int16Array(bytes.buffer, 0, bytes.length >> 1);
}

export class RealtimeClient {
  private ws: WebSocket | null = null;
  /**
   * **モデルは応答を 1 つしか同時に持てない。** 走っている最中に `response.create` を
   * 送ると `conversation_already_has_active_response` で拒否され、**こちらは黙ったまま**
   * になる (2026-09-01 の実機: 読み上げの途中で「聞こえました」を押すと氏名を訊いてこない)。
   * `response.created` / `response.done` で追跡し、必要なときだけ打ち切る。
   */
  private activeResponse = false;

  constructor(private opts: RealtimeClientOpts) {}

  async connect(): Promise<void> {
    let res: Response;
    try {
      res = await fetch(TOKEN_ENDPOINT, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: "{}",
      });
    } catch (e) {
      const err = e as Error;
      this.opts.onError({
        code: "token-failed",
        message: (err?.message ?? "").slice(0, ERROR_MSG_CAP),
      });
      throw e;
    }
    if (!res.ok) {
      this.opts.onError({ code: "token-failed", message: `token fetch failed: ${res.status}` });
      throw new Error(`token fetch failed: ${res.status}`);
    }
    const { token, wsUrl } = (await res.json()) as { token: string; wsUrl: string };

    await new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(wsUrl, ["realtime", `openai-insecure-api-key.${token}`]);
      this.ws = ws;
      ws.onopen = () => resolve();
      ws.onerror = () => {
        this.opts.onError({ code: "ws-failed", message: "websocket error" });
        reject(new Error("websocket error"));
      };
      ws.onclose = () => {
        if (this.ws === ws) this.ws = null;
      };
      ws.onmessage = (ev: MessageEvent) => this.handle(String(ev.data));
    });
  }

  private handle(raw: string): void {
    let msg: Record<string, unknown>;
    try {
      msg = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      // 壊れたメッセージで onmessage ごと死なせない
      return;
    }
    const type = String(msg.type ?? "");
    dlog("←", type, type === "error" ? msg : undefined);
    if (type === "response.created") {
      this.activeResponse = true;
      return;
    }
    if (type === "response.done") {
      this.activeResponse = false;
      const resp = (msg.response ?? {}) as {
        output?: unknown[];
        usage?: unknown;
        status?: unknown;
        status_details?: unknown;
      };
      // **status が無いと「読まなかった」と「打ち切られた」を区別できない。**
      // 2026-09-02 の実機ではここが見えず、probe で再現するまで原因を絞れなかった
      // (モデルが指定した文を読まずに終わる回が実測で約半分ある)。
      dlog("←", `response.done status=${String(resp.status ?? "?")}`, resp.status_details);
      // 実コストの一次データ。53 円/セッションは所要時間からの見積もりでしかない。
      if (resp.usage && typeof resp.usage === "object") {
        this.opts.onUsage(resp.usage as Record<string, unknown>);
      }
      const output = (resp.output ?? []) as Array<Record<string, unknown>>;
      for (const item of output) {
        if (item.type === "function_call") {
          // **type だけの行では function_call が response.done に埋もれる。**
          // 「なぜ進まないのか」はモデルが何を呼んだかで決まるので、名前と引数を出す。
          dlog("←", `tool ${String(item.name ?? "?")}`, item.arguments);
          this.opts.onToolCall({
            name: String(item.name ?? ""),
            args: String(item.arguments ?? ""),
            callId: String(item.call_id ?? ""),
          });
        }
      }
      // **tool の処理より後に通知する** — 先に出すと、無音の計測が張られた直後に
      // tool を受けた呼び出し側が次の task を渡し、張ったばかりのタイマーが宙に浮く。
      this.opts.onResponseDone();
      return;
    }
    if (type === "response.output_audio.delta" || type === "response.audio.delta") {
      const d = msg.delta;
      if (typeof d === "string" && d.length > 0) this.opts.onAudioDelta(fromBase64Pcm16(d));
      return;
    }
    if (
      type === "response.output_audio_transcript.done" ||
      type === "response.audio_transcript.done"
    ) {
      const t = msg.transcript;
      // **何を喋ったかを出す。** 「進まない」の原因は毎回ここで決まる — 指定した文を
      // 読んだのか、「では読み上げますね」で済ませたのかは type の行では分からない。
      dlog("←", "narrated", t);
      if (typeof t === "string" && t.length > 0) this.opts.onNarrated(t);
      return;
    }
    if (type === "conversation.item.input_audio_transcription.completed") {
      const t = msg.transcript;
      if (typeof t === "string" && t.length > 0) this.opts.onTranscript(t);
      return;
    }
    if (type === "input_audio_buffer.speech_started") {
      this.opts.onSpeechStarted();
      return;
    }
    if (type === "input_audio_buffer.speech_stopped") {
      this.opts.onSpeechStopped();
      return;
    }
    if (type === "error") {
      const e = (msg.error ?? {}) as { message?: string };
      this.opts.onError({
        code: "ws-failed",
        message: String(e.message ?? "").slice(0, ERROR_MSG_CAP),
      });
    }
  }

  /**
   * OPEN のときだけ送る。CONNECTING 中の send は実 WS が throw するため。
   *
   * **readyState ガードと try/catch は二重の網である (2026-08-30 の変異検証で実測)** —
   * 片方だけ外しても観測差が出ず、両方外して初めてテストが落ちる。したがって
   * 「try/catch があるからガードは要らない」も「ガードがあるから try/catch は要らない」も
   * 成り立つように見えるが、**どちらも残す**。ガードは無駄な例外生成を避け、
   * try/catch は readyState が OPEN でも失敗しうる経路 (送信バッファ超過など) を拾う。
   */
  private send(payload: unknown): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== 1) return;
    try {
      dlog("→", String((payload as { type?: string }).type ?? "?"));
      ws.send(JSON.stringify(payload));
    } catch {
      // 送れなくても呼び出し元 (マイクのコールバック) は止めない
    }
  }

  updateSession(p: RealtimeSessionPatch): void {
    this.send({
      type: "session.update",
      session: {
        type: "realtime",
        output_modalities: ["audio"],
        instructions: p.instructions,
        tools: p.tools,
        tool_choice: "auto",
        audio: {
          input: {
            format: { type: "audio/pcm", rate: 24000 },
            // semantic_vad は使わない (実測で分断を防げず 2.1 でも改善しない、D5)
            turn_detection: {
              type: "server_vad",
              threshold: 0.5,
              prefix_padding_ms: 300,
              silence_duration_ms: p.silenceDurationMs,
              create_response: true,
              interrupt_response: true,
            },
            transcription: { model: "whisper-1", language: "ja" },
          },
          output: {
            format: { type: "audio/pcm", rate: 24000 },
            // **必ず明示する。** 既定任せだと上流が既定を変えた日に黙って声が変わる。
            // 案内は cascade の mp3 で鳴るので、実機では 2 つの声が交互に出る。
            voice: p.voice ?? REALTIME_VOICE,
          },
        },
      },
    });
  }

  sendAudio(pcm16k: Float32Array): void {
    if (!this.isOpen()) return;
    const pcm24k = upsample16kTo24k(pcm16k);
    for (let i = 0; i < pcm24k.length; i += APPEND_MAX_SAMPLES) {
      this.send({
        type: "input_audio_buffer.append",
        audio: toBase64Pcm16(pcm24k.subarray(i, i + APPEND_MAX_SAMPLES)),
      });
    }
  }

  /**
   * こちらから turn を始める (モデルに話し始めさせる)。
   *
   * **`session.update` を送るだけではモデルは一言も話さない。**
   * `turn_detection.create_response` が効くのは「ユーザーが話し終えた後」だけなので、
   * 設問の読み上げや案内のように**こちらから始める turn** には必ずこれが要る
   * (2026-09-01 の実機確認で、話しかけない限り何も進まない状態になっていた)。
   *
   * **`session.update` の後に送ること。** 逆順だと差し替える前の instructions で話す。
   * WebSocket は順序を保つので `session.updated` の到着は待たない
   * (待つ形が要ると分かったら、そのときに待つ)。
   */
  createResponse(instructions?: string): void {
    // 走っている応答を先に畳む。**順序が仕様** — 先に create を送るとその create 自身が
    // 拒否される。応答が無いときに cancel を送ると上流がエラーを返し、それが onError 経由で
    // 画面をテキスト入力へ落とすので、走っているときだけ送る。
    if (this.activeResponse) {
      this.send({ type: "response.cancel" });
      this.activeResponse = false;
    }
    // instructions を渡すと**この応答にだけ**効く。session.instructions を書き換えると
    // 促した後もずっとその指示のままになり、設問の読み上げが壊れる。
    this.send({
      type: "response.create",
      ...(instructions ? { response: { instructions } } : {}),
    });
  }

  /**
   * tool の結果だけを返す (話し始めさせない)。
   *
   * **tool を呼んだモデルは function_call_output が返るまで次の応答を作らない**ので、
   * 返さないと会話がそこで止まる。一方 **結果を返すことと turn を始めることは別** —
   * 次の設問へ進む場合は setTask 側が turn を始めるため、ここで response.create まで
   * 送ると応答が二重に走る。
   */
  sendToolOutput(callId: string, output: string): void {
    this.send({
      type: "conversation.item.create",
      item: { type: "function_call_output", call_id: callId, output },
    });
  }

  /** 結果を返し、そのまま続きを話させる (同じ task を続ける聞き直しなどで使う)。 */
  respondToTool(callId: string, output: string): void {
    this.sendToolOutput(callId, output);
    this.createResponse();
  }

  close(): void {
    const ws = this.ws;
    this.ws = null;
    try {
      ws?.close();
    } catch {
      // 既に閉じている
    }
  }

  isOpen(): boolean {
    return this.ws !== null && this.ws.readyState === 1;
  }
}
